import { useCallback, useState } from "react";
import { Flex, Button, IconButton, TextField } from "@radix-ui/themes";
import { Plus, X } from "lucide-react";
import { Text } from "@radix-ui/themes";

interface EmailListInputProps {
  emails: string[];
  onChange: (emails: string[]) => void;
}

export function EmailListInput({ emails, onChange }: EmailListInputProps) {
  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const handleAddEmail = useCallback(() => {
    if (newEmail.trim() && !emails.includes(newEmail.trim())) {
      onChange([...emails, newEmail.trim()]);
      setNewEmail("");
      setIsAddingEmail(false);
    }
  }, [newEmail, emails, onChange]);

  const handleRemoveEmail = useCallback(
    (emailToRemove: string) => {
      onChange(emails.filter((email) => email !== emailToRemove));
    },
    [emails, onChange]
  );

  const handleCancel = useCallback(() => {
    setIsAddingEmail(false);
    setNewEmail("");
  }, []);

  const handleStartAdding = useCallback(() => {
    setIsAddingEmail(true);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddEmail();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [handleAddEmail, handleCancel]
  );

  const handleEmailInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewEmail(e.target.value);
    },
    []
  );

  return (
    <Flex direction="column" gap="2">
      <Flex gap="2" wrap="wrap" align="center">
        {emails.map((email) => {
          const onRemove = () => handleRemoveEmail(email);

          return (
            <Flex
              key={email}
              align="center"
              gap="1"
              style={{
                padding: "4px 8px",
                borderRadius: "4px",
                backgroundColor: "var(--gray-3)",
              }}
            >
              <span style={{ fontSize: "12px" }}>{email}</span>
              <IconButton size="1" variant="ghost" onClick={onRemove}>
                <X size={12} />
              </IconButton>
            </Flex>
          );
        })}

        {isAddingEmail ? (
          <Flex gap="1" align="center">
            <TextField.Root
              size="1"
              value={newEmail}
              onChange={handleEmailInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Enter email"
              autoFocus
            />
            <Button size="1" onClick={handleAddEmail}>
              Add
            </Button>
            <Button size="1" variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
          </Flex>
        ) : (
          <Button size="1" variant="soft" onClick={handleStartAdding}>
            <Plus size={12} />
            <Text size="2">Add Email</Text>
          </Button>
        )}
      </Flex>
    </Flex>
  );
}
